/*
 * strings.ts — map the device-ui i18n namespace (device.composer.*) onto the
 * ProductComposerStrings the jcode-ui/product composer consumes. The package
 * itself ships English defaults; every field we return here overrides them,
 * so console/mobile render the composer in the host's five locales. Fields
 * that interpolate are functions on the package side — they close over t().
 */
import type { TFunction } from 'i18next';
import type { ProductComposerStrings } from 'jcode-ui/product';

export function buildProductComposerStrings(t: TFunction): Partial<ProductComposerStrings> {
  const k = (key: string) => t(`device.composer.${key}`);
  return {
    placeholder: k('placeholder'),
    goalPlaceholder: k('goalPlaceholder'),
    queuePlaceholder: k('queuePlaceholder'),
    attachedImages: k('attachedImages'),
    send: k('send'),
    queue: k('queue'),
    stop: k('stop'),
    stopAgent: k('stopAgent'),
    stopAndNext: k('stopAndNext'),
    removeQueued: k('removeQueued'),
    add: k('add'),
    attachFiles: k('attachFiles'),
    command: k('command'),
    goal: k('goal'),
    workflowBadge: k('workflowBadge'),
    goalSlashDesc: k('goalSlashDesc'),
    goalHintNext: k('goalHintNext'),
    goalHintNextReplaces: k('goalHintNextReplaces'),
    goalHintRemove: k('goalHintRemove'),
    goalHintReplace: k('goalHintReplace'),
    modelNoImages: k('modelNoImages'),

    modeApproval: k('modeApproval'),
    modeApprovalSub: k('modeApprovalSub'),
    modePlan: k('modePlan'),
    modePlanSub: k('modePlanSub'),
    modeAuto: k('modeAuto'),
    modeAutoSub: k('modeAutoSub'),
    modeFullAccess: k('modeFullAccess'),
    modeFullAccessSub: k('modeFullAccessSub'),
    modeCeilingHint: k('modeCeilingHint'),

    modelFilter: k('modelFilter'),
    modelCurrent: k('modelCurrent'),
    modelFavorites: k('modelFavorites'),
    modelReasoning: k('modelReasoning'),
    modelTools: k('modelTools'),
    modelImages: k('modelImages'),
    modelNoImageInput: k('modelNoImageInput'),
    modelNone: k('modelNone'),
    modelNoMatch: k('modelNoMatch'),
    modelManage: k('modelManage'),
    modelManageTitle: k('modelManageTitle'),
    modelToggleVisibility: k('modelToggleVisibility'),
    modelVisibleCount: (visible, total) =>
      t('device.composer.modelVisibleCount', { visible, total }),
    effort: k('effort'),
    effortTitle: k('effortTitle'),
    effortDefault: k('effortDefault'),

    contextTitle: k('contextTitle'),
    contextSystemPrompt: k('contextSystemPrompt'),
    contextSystemTools: k('contextSystemTools'),
    contextMcpTools: k('contextMcpTools'),
    contextSkills: k('contextSkills'),
    contextMessages: k('contextMessages'),
    contextInput: k('contextInput'),
    contextOutput: k('contextOutput'),
    contextCached: k('contextCached'),
    contextReasoning: k('contextReasoning'),
    contextCacheHitRate: k('contextCacheHitRate'),

    workspaceNone: k('workspaceNone'),
    workspaceNonePlural: k('workspaceNonePlural'),
    workspaceSearch: k('workspaceSearch'),
    workspaceLoading: k('workspaceLoading'),
    workspaceNoFolders: k('workspaceNoFolders'),
    workspaceOpen: k('workspaceOpen'),
    workspaceOpenFolder: k('workspaceOpenFolder'),
    workspaceOpenError: k('workspaceOpenError'),
    workspacePathPlaceholder: k('workspacePathPlaceholder'),
    remoteConnect: k('remoteConnect'),

    branchesTitle: k('branchesTitle'),
    branchesNone: k('branchesNone'),
    branchSearch: k('branchSearch'),
    branchCreate: k('branchCreate'),
    branchCreateBtn: k('branchCreateBtn'),
    branchNewName: k('branchNewName'),
    branchCurrent: (name) => t('device.composer.branchCurrent', { name }),
    branchConfirmTitle: k('branchConfirmTitle'),
    branchConfirmIntro: (branch) => t('device.composer.branchConfirmIntro', { branch }),
    branchConfirmMore: (count) => t('device.composer.branchConfirmMore', { count }),
    branchConfirmStash: k('branchConfirmStash'),
    branchConfirmDiscard: k('branchConfirmDiscard'),
    branchConfirmCancel: k('branchConfirmCancel'),
    branchConfirmHint: k('branchConfirmHint'),
    branchSwitchError: k('branchSwitchError'),

    goalStatusActive: k('goalStatusActive'),
    goalStatusCompleted: k('goalStatusCompleted'),
    goalStatusBlocked: k('goalStatusBlocked'),
    goalStarted: k('goalStarted'),
    goalElapsed: k('goalElapsed'),
    goalEdit: k('goalEdit'),
    goalEditTitle: k('goalEditTitle'),
    goalClear: k('goalClear'),
    goalSaveFailed: k('goalSaveFailed'),
    goalTokens: (used) => t('device.composer.goalTokens', { used }),
    goalTokensK: (kk) => t('device.composer.goalTokensK', { k: kk }),
    durationSeconds: (n) => t('device.composer.durationSeconds', { n }),
    durationMinutes: (m, s) => t('device.composer.durationMinutes', { m, s }),
    durationHours: (h, m) => t('device.composer.durationHours', { h, m }),

    commonTokens: (used) => t('device.composer.commonTokens', { used }),
    commonLoading: k('commonLoading'),
    commonClose: k('commonClose'),
    commonCancel: k('commonCancel'),
    commonSave: k('commonSave'),
    commonDone: k('commonDone'),
    commonEnable: k('commonEnable'),
    commonDisable: k('commonDisable'),
    commonRecommended: k('commonRecommended'),
  };
}
